// ** React Imports
import { useEffect, useState } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// ** Custom Component Imports
import CustomChip from "components/mui/chip";

// ** Dialog Imports
import PaymentReceiptDialog from './PaymentReceipt';
import RefundDialog from "./Refund";

// Utility: date formatting helpers
import dayjs from "dayjs";
import { getServiceRequests } from "screens/DispatchBoard/logic/DispatchBoardLogic";
import { GetCustomer } from "screens/Customers/logic/GetCustomer";
import { getInvoices } from "../logic/PaymentLogic";

const formatPrice = (val) => {
  if (val === null || val === undefined || val === "") return "N/A";
  const num = typeof val === "number" ? val : Number(val);
  return isNaN(num)
    ? "N/A"
    : `$${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

const PaymentDetail = ({ open, handleClose, payment, RefundProps, fetchPayments }) => {
  const theme = useTheme();
  const { handleOpenRefundDlg } = RefundProps;
  const [openPaymentReceipt, setOpenPaymentReceipt] = useState(false);

  const [customerData, setCustomerData] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [openRefundDlg, setOpenRefundDlg] = useState(false)
  const [refundData, setRefundData] = useState(null)

  const isInvoice = payment.paymentType === "Invoice" || payment.entity === "Invoice";
  const isAdvance = payment.paymentType === "Deposit" || payment.entity === "Advance";
  
  useEffect(() => {
    const fetchCustomer = async () => {
      // Find the Service Request first to get customerID
      setLoadingCustomer(true);
      try {
        const reqs = await getServiceRequests(); // This should fetch all assessments/workorders
        const req =
          payment?.requestType === "WorkOrder"
            ? reqs.find((r) => r.requestID === payment?.requestID && r.entityType === "WorkOrder")
            : reqs.find((r) => r.requestID === payment?.requestID && r.entityType === "Assessment");
  
        if (req && req.customerID) {
          const res = await GetCustomer(req.customerID);
          setCustomerData(res && res[0] ? res[0] : null);
        } else {
          setCustomerData(null);
        }
      } catch {
        setCustomerData(null);
      } finally {
        setLoadingCustomer(false);
      }
    };
  
    if (payment?.requestID && payment?.requestType) {
      fetchCustomer();
    } else {
      setCustomerData(null);
    }
  }, [payment?.requestID, payment?.requestType]);

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!payment || !payment.invoiceID) return setInvoiceData(null);
      setLoadingInvoice(true);
      try {
        
        const invoices = await getInvoices();
        const invoice = invoices.find(inv => inv.invoiceID === payment.invoiceID);
        setInvoiceData(invoice || null);
      } catch (err) {
        setInvoiceData(null);
      } finally {
        setLoadingInvoice(false);
      }
    };
  
    if (isInvoice && payment?.invoiceID > 0) {
      fetchInvoice();
    } else {
      setInvoiceData(null);
    }
  }, [payment?.invoiceID, isInvoice]);

  // For formatting
  const prettyDate = d =>
    d ? dayjs(d).format("MMMM D, YYYY") : "";

  // Dynamically extract fields based on type

  if (!payment) return null;

  const invoiceTotal = Number(invoiceData?.totalAmount) || 0;
  const invoiceDepositUtilized = Number(invoiceData?.depositUtilizedAmount) || 0;
  const invoiceActual = invoiceTotal - invoiceDepositUtilized;
  const remainingRaw = (invoiceActual ?? 0) - (payment?.amount ?? 0);
  const remaining = remainingRaw <= 0 ? 0 : remainingRaw;

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="md"
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: 'center',
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Payment Detail - {isInvoice ? "Invoice" : "Advance"}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {payment.status === "Refunded" ? `REF - ${payment.paymentID}` : `PAY - ${payment.paymentID}`}
            </Typography>
          </Box>
          <Box>
            <CustomChip
              rounded
              size="small"
              skin="light"
              sx={{ lineHeight: 1 }}
              color={
                payment.status === "Paid"
                  ? "success"
                  : payment.status === "Partially Paid"
                  ? "warning"
                  : "secondary"
              }
              label={payment.status}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={5}>
            {/* Invoice Info */}
            {isInvoice && (
            <>
              <Grid item xs={12}>
                <Typography variant="h4">Invoice Information</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ color: "text.disabled" }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" color="text.secondary">
                  Invoice ID
                </Typography>
                <Typography variant="h6">
                  {invoiceData ? `INV-${invoiceData.invoiceID}` : payment.invoiceID ? `INV-${payment.invoiceID}` : ""}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" color="text.secondary">
                  Invoice Amount
                </Typography>
                <Typography variant="h6">
                  {loadingInvoice
                    ? "Loading..."
                    : formatPrice(invoiceData?.totalAmount ?? payment.amount)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" color="text.secondary">
                  Invoice Date
                </Typography>
                <Typography variant="h6">
                  {loadingInvoice
                    ? "Loading..."
                    : invoiceData?.invoiceDate
                       ? prettyDate(invoiceData.invoiceDate)
                       : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" color="text.secondary">
                  Invoice Due Date
                </Typography>
                <Typography variant="h6">
                  {loadingInvoice
                    ? "Loading..."
                    : invoiceData?.dueDate
                       ? prettyDate(invoiceData.dueDate)
                       : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" color="text.secondary">
                  Payment Terms
                </Typography>
                <Typography variant="h6">
                  {loadingInvoice
                    ? "Loading..."
                    : invoiceData?.additionalPaymentTerms?.[0] || "N/A"}
                </Typography>
              </Grid>
              {/* If you want to show customer info from invoice */}
              <Grid item xs={12} md={6}>
                <Typography variant="body1" color="text.secondary">
                  Remaining Amount
                </Typography>
                <Typography variant="h6">
                  {formatPrice(remaining)}
                </Typography>
              </Grid>
            </>
          )}

            {/* Advance Info */}
            {isAdvance && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h4">Advance/Deposit Information</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ color: "text.disabled" }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="h6">
                    {payment.requestType === "Assessment"
                      ? `Assessment (AST-${payment.requestID})`
                      : `Work Order (WO-${payment.requestID})`}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" color="text.secondary">
                    Paid Amount
                  </Typography>
                  <Typography variant="h6">{formatPrice(payment.amount)}</Typography>
                </Grid>
              </>
            )}

            {/* Customer Info */}
            <Grid item xs={12} mt={2}>
              <Typography variant="h4">Customer Information</Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ color: "text.disabled" }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" color="text.secondary">Customer Name</Typography>
              <Typography variant="h6">
                {loadingCustomer
                  ? "Loading..."
                  : customerData
                    ? (customerData.customerType === "Individual"
                        ? `${customerData.firstName || ""} ${customerData.lastName || ""}`
                        : customerData.companyName || "")
                    : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" color="text.secondary">Email</Typography>
              <Typography variant="h6">
                {loadingCustomer ? "Loading..." : customerData?.email || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" color="text.secondary">Phone</Typography>
              <Typography variant="h6">
                {loadingCustomer
                  ? "Loading..."
                  : customerData?.phoneNumbers?.[0]?.phoneNo || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" color="text.secondary">
                Note
              </Typography>
              <Typography variant="h6">{payment.notes || payment.note || "N/A"}</Typography>
            </Grid>

            {/* Payment Info */}
            <Grid item xs={12} mt={2}>
              <Typography variant="h4">Payment Information</Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ color: "text.disabled" }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" color="text.secondary">
                Paid Amount
              </Typography>
              <Typography variant="h6">{formatPrice(payment.amount)}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" color="text.secondary">
                Payment Date
              </Typography>
              <Typography variant="h6">{payment.paymentDate ? prettyDate(payment.paymentDate) : "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" color="text.secondary">
                Payment Method
              </Typography>
              <Typography variant="h6">{payment.paymentMethod || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" color="text.secondary">
                Paid By
              </Typography>
              <Typography variant="h6">{payment.paidBy || "N/A"}</Typography>
            </Grid>
          </Grid>
        </DialogContent>
      <DialogActions
  sx={{
    display: "flex",
    justifyContent: "space-between",
    padding: "20px 24px !important",
    boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
    zIndex: 1,
    '@media (max-width: 600px)': {
      flexDirection: "column",
      gap: 2,
      "& > *": {
        width: "100% !important"
      }
    }
  }}
  className="dialog-actions-dense"
>
  
    <Box sx={{ 
    display: 'flex', 
    gap: 2, 
    alignItems: 'center',
    '@media (max-width: 600px)': {
      flexDirection: "column",
      width: "100%",
      "& > *": {
        width: "100%"
      }
    }
  }}>
    <Button variant="contained" color="info" onClick={handleClose}>
      Cancel
    </Button>
  </Box>

  {/* Right side - unchanged but will stack on mobile */}
  <Box sx={{ 
    display: 'flex', 
    gap: 2, 
    alignItems: 'center',
    // Mobile-specific styles
    '@media (max-width: 600px)': {
      flexDirection: "column",
      width: "100%",
      "& > *": {
        width: "100%"
      }
    }
  }}>
    <Button variant="tonal" color="info" onClick={() => setOpenPaymentReceipt(true)}>
      Preview Receipt
    </Button>
    <Button 
      variant="tonal" 
      color="info" 
      onClick={() => handleOpenRefundDlg(payment)}
      disabled={payment.status === "Refunded"}            
    >
      Issue Refund
    </Button>
    <Button variant="contained" color="main" onClick={handleClose}>
      Send Receipt
    </Button>
  </Box>
</DialogActions>
      </Dialog>

      <PaymentReceiptDialog
        open={openPaymentReceipt}
        handleClose={() => setOpenPaymentReceipt(false)}
        handleClosePaymentDetail={handleClose}
        entity="detail"
        payment={payment}
        customerData={customerData}
        invoiceData={invoiceData}
        fetchPayments={fetchPayments}
      />

      <RefundDialog 
        invoiceID={payment.invoiceID}
        RefundProps={RefundProps}
        handleClosePaymentDetail={handleClose} 
        fetchPayments={fetchPayments}
      />
    </>
  );
};

export default PaymentDetail;
